import { createContext, useContext } from 'react';
import { Session, User } from '@supabase/supabase-js';
import type { Profile, Club } from '@/types/crm';
import type { ViewScope } from '@/types/viewScope';

/**
 * M-1 PR-d: AuthContext reduzido a tipos + Context + hook.
 *
 * O Provider agora vive em `./AuthBridge.tsx` e usa exclusivamente
 * `@pps/auth-client` (sem fallback legado, sem feature flag). O shim
 * blue-green (AuthContext.shim.tsx) e a flag VITE_USE_PPS_AUTH foram
 * removidos após HML 5/5 OK.
 *
 * Os 66 call sites continuam importando `useAuth` daqui sem alteração —
 * o `AuthBridge` republica os valores neste mesmo Context.
 */
export interface AuthContextType {
  session: Session | null;
  user: User | null;
  profile: Profile | null;
  club: Club | null;
  clubs: Club[];
  staffRole: string | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, fullName: string) => Promise<void>;
  signOut: () => Promise<void>;
  selectClub: (club: Club) => void;
  // Multi-club / rede (Bloco A.1)
  viewScope: ViewScope;
  setViewScope: (s: ViewScope) => void;
  networkClubs: Club[];
  networkClubIds: string[];
  isNetworkAdmin: boolean;
  /** Recarrega o profile do banco (após editar nome/foto em /minha-conta). */
  refreshProfile: () => Promise<void>;
}

export type { Profile, Club };

export const AuthContext = createContext<AuthContextType | null>(null);

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
